/**
 * 元素公共 Zod 片段（layout / appearance / interaction 等）。
 */
import { z } from 'zod';

export const PointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export const BoxSpacingSchema = z.object({
  top: z.number().default(0),
  right: z.number().default(0),
  bottom: z.number().default(0),
  left: z.number().default(0),
});

export const ElementMetaSchema = z.object({
  name: z.string().optional(),
});

export const ElementLayoutSchema = z.object({
  x: z.number().default(0),
  y: z.number().default(0),
  zIndex: z.number().default(0),
  parentId: z.string().optional(),
  rotation: z.number().default(0),
  scaleX: z.number().optional(),
  scaleY: z.number().optional(),
  flipX: z.boolean().optional(),
  flipY: z.boolean().optional(),
  transformOrigin: PointSchema.optional(),
  rotate3d: z
    .object({
      x: z.number().optional(),
      y: z.number().optional(),
      z: z.number().optional(),
    })
    .optional(),
});

export const ElementSizeSchema = z.object({
  width: z.number().default(100),
  height: z.number().default(100),
  minWidth: z.number().optional(),
  minHeight: z.number().optional(),
  maxWidth: z.number().optional(),
  maxHeight: z.number().optional(),
  fit: z.enum(['fixed', 'hug', 'fill']).optional(),
  overflow: z.enum(['visible', 'hidden', 'scroll', 'auto']).optional(),
  scrollX: z.number().optional(),
  scrollY: z.number().optional(),
  padding: BoxSpacingSchema.optional(),
});

export const PaintSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('solid'), color: z.string(), opacity: z.number().optional() }),
  z.object({ type: z.literal('gradient'), value: z.string(), opacity: z.number().optional() }),
  z.object({ type: z.literal('image'), src: z.string(), opacity: z.number().optional() }),
]);

export const StrokeSchema = z.object({
  color: z.string(),
  width: z.number(),
  style: z.enum(['solid', 'dashed', 'dotted', 'double']).optional(),
  opacity: z.number().optional(),
  placement: z.enum(['center', 'inside', 'outside']).optional(),
});

export const CornerRadiusSchema = z.object({
  all: z.number().optional(),
  topLeft: z.number().optional(),
  topRight: z.number().optional(),
  bottomRight: z.number().optional(),
  bottomLeft: z.number().optional(),
});

export const ShadowEffectSchema = z.object({
  type: z.enum(['drop', 'inner']).default('drop'),
  color: z.string(),
  offsetX: z.number(),
  offsetY: z.number(),
  blur: z.number(),
  spread: z.number().optional(),
});

export const FilterEffectSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('blur'), radius: z.number() }),
  z.object({ type: z.literal('css'), value: z.string() }),
]);

export const ElementAppearanceSchema = z.object({
  opacity: z.number().default(1),
  fills: z.array(PaintSchema).optional(),
  strokes: z.array(StrokeSchema).optional(),
  radius: CornerRadiusSchema.optional(),
  shadows: z.array(ShadowEffectSchema).optional(),
  filters: z.array(FilterEffectSchema).optional(),
  blendMode: z
    .enum([
      'normal',
      'multiply',
      'screen',
      'overlay',
      'darken',
      'lighten',
      'color-dodge',
      'color-burn',
      'hard-light',
      'soft-light',
      'difference',
      'exclusion',
    ])
    .optional(),
});

export const InteractionActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({
    type: z.literal('open-url'),
    href: z.string(),
    target: z.enum(['_self', '_blank']).optional(),
  }),
  z.object({ type: z.literal('go-to-page'), pageId: z.string() }),
]);

export const ElementInteractionSchema = z.object({
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  selectable: z.boolean().optional(),
  draggable: z.boolean().optional(),
  resizable: z.boolean().optional(),
  rotatable: z.boolean().optional(),
  pointerEvents: z.enum(['auto', 'none']).optional(),
  cursor: z.string().optional(),
  href: z.string().optional(),
  target: z.enum(['_self', '_blank']).optional(),
  onClickAction: InteractionActionSchema.optional(),
});

export const ElementAnimationSchema = z.object({
  id: z.string(),
  trigger: z.enum(['enter', 'hover', 'click', 'timeline']),
  type: z.string(),
  duration: z.number(),
  delay: z.number().optional(),
  easing: z.string().optional(),
  repeat: z.union([z.number(), z.literal('infinite')]).optional(),
  direction: z.enum(['normal', 'reverse', 'alternate']).optional(),
});

export const TypographyStyleSchema = z.object({
  fontFamily: z.string().default('system-ui'),
  fontSize: z.number().default(16),
  fontWeight: z.union([z.string(), z.number()]).default(400),
  fontStyle: z.enum(['normal', 'italic']).optional().default('normal'),
  color: z.string().default('#000000'),
  letterSpacing: z.number().optional(),
  lineHeight: z.union([z.number(), z.string()]).optional(),
});

export const ListStyleSchema = z.object({
  markerGap: z.number().default(8),
  indent: z.number().default(24),
  unorderedMarker: z.enum(['disc', 'circle', 'square']).default('disc'),
});

export const ParagraphStyleSchema = z.object({
  textAlign: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  writingMode: z.enum(['horizontal-tb', 'vertical-rl']).optional(),
  placeholder: z.string().optional(),
  list: ListStyleSchema.optional(),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
  fitWidth: z.boolean().optional(),
  fitHeight: z.boolean().optional(),
  singleLine: z.boolean().optional(),
});

export const BaseElementSchema = z.object({
  id: z.string(),
  version: z.literal(1),
  meta: ElementMetaSchema,
  layout: ElementLayoutSchema,
  size: ElementSizeSchema,
  appearance: ElementAppearanceSchema,
  typography: TypographyStyleSchema,
  paragraph: ParagraphStyleSchema,
  interaction: ElementInteractionSchema.default({ visible: true, locked: false }),
  animations: z.array(ElementAnimationSchema).optional(),
});
